/**
 * AdminPaybill — Super Admin reconciliation queue for M-Pesa Paybill
 * payments. Shows pending submissions, approve/reject controls, and
 * an aggregate KPI strip.
 *
 * Approving a submission activates the user's subscription at
 * T+1 midnight UTC per the compliance spec.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, CheckCircle2, XCircle, Clock, Coins } from "lucide-react";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import PageHeader from "@/components/dashboard/PageHeader";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

const STATUS_LABEL = {
    pending_super_admin_verification: "Pending",
    approved: "Approved",
    rejected: "Rejected",
};

export default function AdminPaybill() {
    const [rows, setRows] = useState(null);
    const [stats, setStats] = useState(null);
    const [status, setStatus] = useState("pending_super_admin_verification");
    const [q, setQ] = useState("");
    const [rejecting, setRejecting] = useState(null); // submission being rejected
    const [rejectNote, setRejectNote] = useState("");
    const [paybill, setPaybill] = useState({ paybill_number: "", account_number: "" });
    const { sorted: sortedRows, sortKey, direction, requestSort } = useSortableData(rows, {
        accessors: {
            user: (r) => r.full_name || r.email || "",
            plan: (r) => r.plan_code || "",
            amount: (r) => Number(r.amount_kes || 0),
            ref: (r) => r.mpesa_ref || "",
            submitted: (r) => r.submitted_at || "",
        },
    });

    async function load() {
        setRows(null);
        const p = new URLSearchParams();
        if (status) p.set("status", status);
        if (q) p.set("q", q);
        try {
            const [q1, q2, cfg] = await Promise.all([
                api.get(`/admin/paybill/queue?${p.toString()}`),
                api.get(`/admin/paybill/stats`),
                api.get(`/paybill/config`).catch(() => null),
            ]);
            setRows(q1.data.rows || []);
            setStats(q2.data);
            if (cfg?.data) setPaybill({
                paybill_number: cfg.data.paybill_number || "",
                account_number: cfg.data.account_number || "",
            });
        } catch { setRows([]); toast.error("Could not load queue"); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

    async function approve(sub) {
        if (!window.confirm(
            `Approve ${sub.email} for ${sub.plan_code} · KES ${sub.amount_kes.toLocaleString()}?`
            + `\n\nActivation starts at the next midnight (UTC).`,
        )) return;
        try {
            const { data } = await api.post(`/admin/paybill/${sub.id}/verify`, { action: "approve" });
            toast.success(`Approved — activates ${new Date(data.activation_starts_at).toLocaleString()}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Approval failed");
        }
    }

    async function reject() {
        if (!rejecting) return;
        try {
            await api.post(`/admin/paybill/${rejecting.id}/verify`,
                            { action: "reject", note: rejectNote || "Payment not verified" });
            toast.success("Rejected");
            setRejecting(null); setRejectNote("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rejection failed");
        }
    }

    const widgets = [
        {
            id: "pb-summary", title: "Reconciliation summary", category: "Overview", layout: { x: 0, y: 0, w: 12, h: 3 }, minW: 6, minH: 2, noPadding: true,
            render: () => (
                stats ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3">
                        <Kpi test="pb-kpi-pending"  icon={<Clock className="w-4 h-4" />}         label="Pending"      value={stats.pending.count}  sub={`KES ${stats.pending.amount.toLocaleString()}`}  color="text-amber-500" />
                        <Kpi test="pb-kpi-approved" icon={<CheckCircle2 className="w-4 h-4" />} label="Approved"     value={stats.approved.count} sub={`KES ${stats.approved.amount.toLocaleString()}`} color="text-emerald-500" />
                        <Kpi test="pb-kpi-rejected" icon={<XCircle className="w-4 h-4" />}       label="Rejected"     value={stats.rejected.count} sub={`KES ${stats.rejected.amount.toLocaleString()}`} color="text-danger" />
                    </div>
                ) : null
            ),
        },
        {
            id: "pb-table", title: "Payment queue", category: "Payments", layout: { x: 0, y: 3, w: 12, h: 14 }, minW: 6, minH: 6,
            render: () => (
                <div className="rounded-xl border-2 border-border bg-card overflow-x-auto" data-testid="pb-table">
                    <table className="w-full text-sm min-w-[720px]">
                        <thead className="bg-card border-b border-border">
                            <tr>
                                <SortableTh label="User" sortKey="user" currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                                <SortableTh label="Plan" sortKey="plan" currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                                <SortableTh label="Amount" sortKey="amount" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="text-xs uppercase tracking-widest" />
                                <SortableTh label="M-Pesa Ref" sortKey="ref" currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                                <SortableTh label="Submitted" sortKey="submitted" currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                                <th className="text-right px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows === null ? (
                                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>
                            ) : sortedRows.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No payments match.</td></tr>
                            ) : sortedRows.map((r) => (
                                <tr key={r.id} className="border-t border-border" data-testid={`pb-row-${r.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold">{r.full_name || "—"}</div>
                                        <div className="text-xs text-muted-foreground break-all">{r.email}</div>
                                    </td>
                                    <td className="px-4 py-3 text-xs uppercase">{r.plan_code}</td>
                                    <td className="px-4 py-3 text-right mono font-bold">{r.amount_kes.toLocaleString()}</td>
                                    <td className="px-4 py-3 mono text-xs">{r.mpesa_ref}</td>
                                    <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(r.submitted_at).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right">
                                        {r.status === "pending_super_admin_verification" ? (
                                            <div className="flex gap-1.5 justify-end">
                                                <Button
                                                    size="sm"
                                                    onClick={() => approve(r)}
                                                    data-testid={`pb-approve-${r.id}`}
                                                >
                                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => { setRejecting(r); setRejectNote(""); }}
                                                    data-testid={`pb-reject-${r.id}`}
                                                >
                                                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                                                </Button>
                                            </div>
                                        ) : (
                                            <span
                                                className={
                                                    "inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded " +
                                                    (r.status === "approved"
                                                        ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/40"
                                                        : "bg-danger/15 text-danger border border-danger/40")
                                                }
                                            >
                                                {r.status}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-6" data-testid="admin-paybill">
                <PageHeader
                    icon={Coins}
                    eyebrow="Admin Tools"
                    title="Paybill"
                    subtitle={
                        <>
                            {paybill.paybill_number ? (
                                <>Verify M-Pesa payments against Paybill <span className="mono">{paybill.paybill_number}</span> · Account <span className="mono">{paybill.account_number}</span>.</>
                            ) : (
                                <>Verify M-Pesa payments against the configured Paybill.</>
                            )}{" "}
                            Approvals activate subscriptions at the next midnight UTC.
                        </>
                    }
                    testId="admin-paybill-header"
                />

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2">
                    {["pending_super_admin_verification", "approved", "rejected", ""].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatus(s)}
                            data-testid={`pb-tab-${s || "all"}`}
                            className={
                                "px-3 h-8 rounded text-xs font-bold border transition-colors " +
                                (status === s ? "bg-primary/20 border-primary text-primary" : "border-border text-muted-foreground")
                            }
                        >
                            {STATUS_LABEL[s] || "All"}
                        </button>
                    ))}
                    <div className="relative ml-auto">
                        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search email or M-Pesa ref"
                            className="pl-8 w-64"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            data-testid="pb-search-input"
                        />
                    </div>
                </div>

                <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-paybill" widgets={widgets} />

                {/* Reject dialog */}
                {rejecting && (
                    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
                         onClick={() => setRejecting(null)}>
                        <div className="bg-card border-2 border-border rounded-2xl p-6 max-w-md w-full"
                             onClick={(e) => e.stopPropagation()}
                             data-testid="pb-reject-dialog">
                            <div className="text-lg font-bold">Reject payment</div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {rejecting.email} · Ref {rejecting.mpesa_ref}
                            </p>
                            <Input
                                className="mt-3"
                                placeholder="Reason (visible to the user)"
                                value={rejectNote}
                                onChange={(e) => setRejectNote(e.target.value)}
                                data-testid="pb-reject-note"
                            />
                            <div className="flex justify-end gap-2 mt-4">
                                <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
                                <Button variant="destructive" onClick={reject} data-testid="pb-reject-confirm">
                                    Reject
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
}

function Kpi({ label, value, sub, color, icon, test }) {
    return (
        <div className="rounded-xl border-2 border-border bg-card p-4" data-testid={test}>
            <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${color}`}>
                {icon} {label}
            </div>
            <div className={`text-3xl font-extrabold tracking-tight mt-1 mono whitespace-nowrap ${color}`}>{value}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 break-words">{sub}</div>
        </div>
    );
}
